import {
  AutoGraph,
  ChevronLeft,
  People,
  PeopleOutline,
  Person,
  PersonOutline,
  PickleBall,
  ThumbDown,
  ThumbUp,
} from '@/components/icons'
import { StatsLoading } from '@/components/skeleton'
import { useGetRatingHistory, useGetUserStats } from '@/services'
import { ProvisionalRatings } from '@/types'
import {
  CategoryScale,
  Chart as ChartJS,
  ChartType,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  TooltipPositionerFunction,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'
import { ProvisionalSymbol } from '../icons/ProvisionalSymbol'
import ReliabilityScore from './ReliabilityScore'

declare module 'chart.js' {
  interface TooltipPositionerMap {
    custom: TooltipPositionerFunction<ChartType>
  }
}

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ChartDataLabels)

Tooltip.positioners.custom = (_elements: any, eventPosition: any) => {
  const chart = document.getElementById('rating-line-graph')
  const chartHeight = chart?.offsetHeight

  if (_elements.length > 0) {
    let tooltip_y = _elements[0].element.y
    if (chartHeight && eventPosition.y >= chartHeight / 2) {
      tooltip_y -= 10
    } else if (chartHeight && eventPosition.y < chartHeight / 2) {
      tooltip_y += 70
    }
    return {
      x: _elements[0].element.x,
      y: tooltip_y,
    }
  }
  return eventPosition
}

type Props = {
  userId: number
  statsDoubles: string
  statsSingles: string
  statsSinglesReliability: string
  statsDoublesReliability: string
  statsProvisional?: ProvisionalRatings
}

type GraphData = {
  x: string
  y: number
}[]

type GraphLabel = {
  text: string
  date: string
}

// const filterOptions = [
//   {
//     key: 'week',
//     value: 'WEEK',
//     label: 'Week',
//   },
//   {
//     key: 'month',
//     value: 'MONTH',
//     label: 'Month',
//   },
// ]

const NON_AI_COACH_IDS = ['95', '90']

/** Checks whether a provisional rating was assigned by an AI coach. */
function isAiRating(coachId?: string): boolean {
  return coachId != null && !NON_AI_COACH_IDS.includes(coachId)
}

export const Statistical = ({
  userId,
  statsDoubles,
  statsSingles,
  statsDoublesReliability,
  statsSinglesReliability,
  statsProvisional,
}: Props) => {
  const { t } = useTranslation()

  const today = moment()
  const daysInWeek = 7

  const isHeightTablet = useMediaQuery('(max-height:820px)')

  const [nextRatingHistory, setNextRatingHistory] = useState<{ [key: string]: number }>({})
  const [isSingles, setIsSingles] = useState<boolean>(false)
  const [filterBy] = useState<'MONTH' | 'WEEK'>('WEEK')
  const [endDate, setEndDate] = useState<moment.Moment>(filterBy === 'WEEK' ? today : moment().endOf('month'))
  const [startDate, setStartDate] = useState<moment.Moment>(
    filterBy === 'WEEK' ? today.clone().subtract(daysInWeek, 'days') : moment().startOf('month')
  )
  const daysInMonth = startDate.daysInMonth()
  const [graphData, setGraphData] = useState<GraphData>([])
  const [graphLabels, setGraphLabels] = useState<GraphLabel[]>([])

  useEffect(() => {
    const today = moment()
    setEndDate(filterBy === 'WEEK' ? today.clone() : moment().clone().endOf('month'))
    setStartDate(filterBy === 'WEEK' ? today.clone().subtract(daysInWeek, 'days') : moment().clone().startOf('month'))
  }, [filterBy])

  const { data: userStats, isLoading } = useGetUserStats(userId)

  const {
    data: ratingHistoryData,
    isSuccess: isGetRatingHistorySuccess,
    isLoading: isGetRatingHistoryLoading,
  } = useGetRatingHistory(
    userId as number,
    {
      endDate: endDate.format('YYYY-MM-DD'),
      limit: 100,
      offset: 0,
      startDate: startDate.clone().subtract(2, 'months').format('YYYY-MM-DD'),
      sortBy: 'asc',
      type: isSingles ? 'SINGLES' : 'DOUBLES',
    },
    isSingles ? 'SINGLES' : 'DOUBLES',
    startDate.format('YYYY-MM-DD'),
    endDate.format('YYYY-MM-DD')
  )

  const shouldShowSinglesGraph = isSingles && statsSingles !== 'NR' && !isGetRatingHistoryLoading
  const shouldShowDoublesGraph = !isSingles && statsDoubles !== 'NR' && !isGetRatingHistoryLoading

  useEffect(() => {
    if (ratingHistoryData) {
      let data: any[] = []
      const dayLabelFormat = filterBy === 'WEEK' ? 'ddd' : 'DD'

      const dayCount = filterBy === 'WEEK' ? daysInWeek : daysInMonth
      const labels: GraphLabel[] = []

      for (let i = 0; i < dayCount; i++) {
        const day = startDate.clone().add(filterBy === 'WEEK' ? i + 1 : i, 'days')
        labels.push({
          text: day.format(dayLabelFormat),
          date: day.format('YYYY-MM-DD'),
        })
      }
      setGraphLabels(labels)

      let startIndex = ratingHistoryData.findIndex((item) => item.date >= labels[0].date)

      if (startIndex >= 1 && ratingHistoryData[startIndex].date > labels[0].date) {
        startIndex = startIndex - 1
      }

      let lastRatingDate = ''
      ratingHistoryData
        .slice(startIndex)
        .reverse()
        .forEach((item) => {
          if (lastRatingDate !== item.date && item.rating !== null) {
            lastRatingDate = item.date
            data.unshift({ ...item, rating: item.rating.toFixed(3) })
          }
        })

      if (data.length > 0) {
        if (data[0].date > labels[0].date) {
          data.unshift({
            ...data[0],
            date: labels[0].text,
          })
        }

        data = data.map((item) => ({
          x: labels.find((label) => label.date == item.date)?.text || labels[0].text,
          y: item.rating,
        }))

        if (Object.keys(nextRatingHistory).length > 0 && data[data.length - 1].x !== labels[labels.length - 1].text) {
          const rating =
            nextRatingHistory[
              `${startDate.clone().add(dayCount, 'days').format('YYYY-MM-DD')}-${filterBy}-${isSingles}`
            ]
          if (rating) {
            data = [
              ...data,
              {
                x: labels[labels.length - 1].text,
                y: rating,
              },
            ]
          }
        }

        if (
          endDate.format('YYYY-MM-DD') >= today.format('YYYY-MM-DD') &&
          labels.find((item) => item.text == data[data.length - 1].x)?.date !== today.format('YYYY-MM-DD')
        ) {
          data = [
            ...data,
            {
              x: today.format(dayLabelFormat),
              y: isSingles ? statsSingles : statsDoubles,
            },
          ]
        }
      } else {
        if (
          nextRatingHistory[`${startDate.clone().add(dayCount, 'days').format('YYYY-MM-DD')}-${filterBy}-${isSingles}`]
        ) {
          data = [
            ...data,
            {
              x: labels[0].text,
              y: nextRatingHistory[
                `${startDate.clone().add(dayCount, 'days').format('YYYY-MM-DD')}-${filterBy}-${isSingles}`
              ],
            },
            {
              x: labels[labels.length - 1].text,
              y: nextRatingHistory[
                `${startDate.clone().add(dayCount, 'days').format('YYYY-MM-DD')}-${filterBy}-${isSingles}`
              ],
            },
          ]
        } else {
          data = [
            ...data,
            {
              x: labels[0].text,
              y: isSingles ? statsSingles : statsDoubles,
            },
            {
              x: labels[labels.length - 1].text,
              y: isSingles ? statsSingles : statsDoubles,
            },
          ]
        }
      }

      data.length > 0 &&
        setNextRatingHistory((prev) => ({
          ...prev,
          [`${startDate.format('YYYY-MM-DD')}-${filterBy}-${isSingles}`]: data[0].y,
        }))
      setGraphData(data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingHistoryData, daysInMonth, filterBy, isSingles])

  const dayLabelsLimit = filterBy === 'WEEK' ? daysInWeek : 5

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        display: true,
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
          stepSize: 0.4,
          font: {
            size: 12,
            weight: 'medium',
          },
        },
      },
      x: {
        display: true,
        border: {
          display: false,
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
          font: {
            size: 12,
            weight: '500',
          },
          autoSkip: true,
          maxTicksLimit: dayLabelsLimit,
        },
      },
    },
    plugins: {
      datalabels: {
        display: false,
        color: 'white',
        align: 'left',
        anchor: 'end',
        font: {
          weight: '500',
        },
      },
      tooltip: {
        enabled: true,
        events: ['click'],
        yAlign: 'bottom',
        xAlign: 'center',
        position: 'custom',
        padding: 10,
        titleColor: '#3A79E0',
        titleFont: { size: 11, weight: 'bold' },
        backgroundColor: '#FFFFFF',
        bodyColor: '#3A79E0',
        cornerRadius: 16,
        bodyFont: { size: 16, weight: 'bold' },
        caretSize: 0,
        displayColors: false,
        callbacks: {
          title: (context: any) => {
            const label = context[0].label
            const labelDate = graphLabels.find((item) => item.text === label)?.date
            if (labelDate) {
              return moment(labelDate).format('MMM DD')
            }
            return label
          },
          label: (context: any) => {
            return context.parsed.y.toFixed(3)
          },
        },
      },
      legend: {
        display: false,
      },
    },
  }

  const data = {
    labels: graphLabels.map((item) => item.text),
    datasets: [
      {
        label: '',
        data: graphData,
        borderColor: '#FFFFFF',
        showLabel: false,
        borderWidth: 2,
        pointRadius: [4, 4, 4, 4, 4, 4, 4],
        pointBackgroundColor: '#3A79E0',
        pointStyle: 'circle',
      },
    ],
  }

  const timeNavigator = () => {
    const onBack = () => {
      if (filterBy === 'WEEK') {
        setStartDate((prev) => prev.clone().subtract(7, 'days'))
        setEndDate((prev) => prev.clone().subtract(7, 'days'))
      } else {
        setStartDate((prev) => moment(prev.clone().subtract(1, 'months')).startOf('month'))
        setEndDate((prev) => moment(prev.clone().subtract(1, 'months')).endOf('month'))
      }
    }
    const onForward = () => {
      if (filterBy === 'WEEK') {
        setStartDate((prev) => prev.clone().add(daysInWeek, 'days'))
        setEndDate((prev) => prev.clone().add(daysInWeek, 'days'))
      } else {
        setStartDate((prev) => moment(prev.clone().add(1, 'months')).startOf('month'))
        setEndDate((prev) => moment(prev.clone().add(1, 'months')).endOf('month'))
      }
    }

    return (
      <div className="w-full flex flex-row items-center gap-x-2">
        <button
          type="button"
          className="rounded-full bg-white bg-opacity-10 h-[36px] w-[36px] cursor-pointer disabled:opacity-50"
          onClick={onBack}
        >
          <ChevronLeft className="text-white w-full" height={24} width={24} />
        </button>
        <p className="text-md font-bold text-white text-opacity-50">
          {startDate
            .clone()
            .add(filterBy === 'WEEK' ? 1 : 0, 'days')
            .format('MMM DD')}{' '}
          - {endDate.format('MMM DD')}
        </p>
        {endDate.format('YYYY-MM-DD') < today.format('YYYY-MM-DD') && (
          <button
            className="rounded-full cursor-pointer bg-white bg-opacity-10 h-[36px] w-[36px] disabled:opacity-50"
            onClick={onForward}
          >
            <ChevronLeft className="rotate-180 text-white w-full" height={24} width={24} />
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="w-full md:w-1/3 flex flex-col gap-y-4 md:gap-y-6">
      <div
        className={`sticky statistical ${
          isHeightTablet
            ? 'statistical-with-header-height-tablet top-72 sm:top-10 lg:top-[72px]'
            : 'statistical-with-header'
        }`}
      >
        <h2 className="text-white text-2xl font-bold mb-2">{t('common.rating')}</h2>

        <div className="flex gap-x-2">
          {isLoading && (
            <>
              <StatsLoading />
              <StatsLoading />
            </>
          )}
          {!isLoading && (
            <div className={'flex  w-full '}>
              <div
                style={{ minWidth: '116px', justifyContent: 'space-between' }}
                className={`pl-2 pr-1 pt-1 pb-2 ${
                  isSingles ? 'opacity-50' : 'bg-white bg-opacity-20'
                }  rounded-lg cursor-pointer flex min-width-116`}
                onClick={() => setIsSingles(false)}
              >
                <div className="pr-1.5">
                  <div className="flex w-full">
                    <People className="text-white" />
                    <span className="text-xs font-medium text-white ml-1">{t('common.doubles')}</span>
                  </div>
                  {statsProvisional?.doublesRating !== null && statsDoubles === 'NR' ? (
                    <p className="text-2xl font-bold text-white py-1">{statsProvisional?.doublesRating}</p>
                  ) : (
                    <p className="text-2xl font-bold text-white py-1">{statsDoubles}</p>
                  )}
                </div>
                {statsProvisional?.doublesRating !== null && statsDoubles === 'NR' ? (
                  isAiRating(statsProvisional?.coach?.id) ? (
                    <img src="/img/ai-video-outline.png" alt="AI Rating" className="w-6 h-6" />
                  ) : (
                    <ProvisionalSymbol />
                  )
                ) : (
                  <ReliabilityScore progress={Number(statsDoublesReliability) | 0} />
                )}
              </div>

              <div
                style={{ minWidth: '116px', justifyContent: 'space-between' }}
                className={`pl-2 pr-1 pt-1 pb-2 ${
                  isSingles ? 'bg-white bg-opacity-20' : 'opacity-50'
                }  rounded-lg cursor-pointer flex `}
                onClick={() => setIsSingles(true)}
              >
                <div className="pr-1.5">
                  <div className="flex w-full">
                    <Person className="text-white" />
                    <span className="text-xs font-medium text-white ml-1">{t('common.singles')}</span>
                  </div>
                  {statsProvisional?.singlesRating !== null && statsSingles === 'NR' ? (
                    <p className="text-2xl font-bold text-white py-1">{statsProvisional?.singlesRating}</p>
                  ) : (
                    <p className="text-2xl font-bold text-white py-1">{statsSingles}</p>
                  )}
                </div>
                {statsProvisional?.singlesRating !== null && statsSingles === 'NR' ? (
                  isAiRating(statsProvisional?.coach?.id) ? (
                    <img src="/img/ai-video-outline.png" alt="AI Rating" className="w-6 h-6" />
                  ) : (
                    <ProvisionalSymbol />
                  )
                ) : (
                  <ReliabilityScore progress={Number(statsSinglesReliability) | 0} />
                )}
              </div>
            </div>
          )}
        </div>

        {shouldShowSinglesGraph && (
          <>
            <div className="mt-4">{timeNavigator()}</div>
            <div className="mt-4">
              {isGetRatingHistorySuccess && graphData.length > 0 && graphLabels.length > 0 && (
                <Line id="rating-line-graph" options={options} data={data} height="192px" />
              )}
            </div>
          </>
        )}

        {shouldShowDoublesGraph && (
          <>
            <div className="mt-4 sm:mt-4 lg:mt-4">{timeNavigator()}</div>
            <div className="mt-4 sm:mt-4 lg:mt-4">
              {isGetRatingHistorySuccess && graphData.length > 0 && graphLabels.length > 0 && (
                <Line id="rating-line-graph" options={options} data={data} height="192px" />
              )}
            </div>
          </>
        )}

        <div
          className={`flex flex-row md:flex-col md:gap-y-5 ${
            isHeightTablet ? 'md:h-[calc(100vh-510px)] pr-2' : 'md:h-auto'
          } md:overflow-y-auto lg:overflow-y-hidden lg:h-auto lg:gap-y-6 mt-4`}
        >
          <div className="flex flex-col flex-1 md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center">
              <ThumbUp className="text-white" />
              <span className="text-xs font-medium md:text-sm md:font-normal text-white md:ml-2 mt-2 md:mt-0">
                {t('performance.wins')}
              </span>
            </div>
            {isLoading ? (
              <div className="animate-pulse dark:bg-slate-400 w-6 h-3 rounded-full"></div>
            ) : (
              <span className="text-base font-semibold md:text-sm md:font-normal text-white">
                {isSingles ? userStats?.singles?.wins : userStats?.doubles?.wins}
              </span>
            )}
          </div>

          <div className="flex flex-col flex-1 md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center">
              <ThumbDown className="text-white" />
              <span className="text-xs font-medium md:text-sm md:font-normal text-white md:ml-2 mt-2 md:mt-0">
                {t('performance.losses')}
              </span>
            </div>
            {isLoading ? (
              <div className="animate-pulse dark:bg-slate-400 w-6 h-3 rounded-full"></div>
            ) : (
              <span className="text-base font-semibold md:text-sm md:font-normal text-white">
                {isSingles ? userStats?.singles?.losses : userStats?.doubles?.losses}
              </span>
            )}
          </div>

          <div className="flex flex-col flex-1 md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center">
              <PickleBall className="text-white" />
              <span className="text-sm font-normal text-white ml-2 hidden md:inline">{t('performance.avgPoints')}</span>
              <span className="text-xs font-medium text-white mt-2 inline md:hidden">{t('performance.avgPts')}</span>
            </div>
            {isLoading ? (
              <div className="animate-pulse dark:bg-slate-400 w-6 h-3 rounded-full"></div>
            ) : (
              <span className="text-base font-semibold md:text-sm md:font-normal text-white">
                {isSingles ? userStats?.singles?.averagePointsWonPercent : userStats?.doubles?.averagePointsWonPercent}
              </span>
            )}
          </div>

          <div className="flex flex-col flex-1 md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center">
              <PeopleOutline className="text-white" />
              <span className="text-sm font-normal text-white ml-2 hidden md:inline">
                {t('performance.avgPartner')}
              </span>
              <span className="text-xs font-medium text-white mt-2 inline md:hidden">{t('performance.avgPtr')}</span>
            </div>
            {isLoading ? (
              <div className="animate-pulse dark:bg-slate-400 w-6 h-3 rounded-full"></div>
            ) : (
              <span className="text-base font-semibold md:text-sm md:font-normal text-white">
                {isSingles ? userStats?.singles?.averagePartnerDupr : userStats?.doubles?.averagePartnerDupr}
              </span>
            )}
          </div>

          <div className="flex flex-col flex-1 md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center">
              <PersonOutline className="text-white" />
              <span className="text-sm font-normal text-white ml-2 hidden md:inline">
                {t('performance.avgOpponent')}
              </span>
              <span className="text-xs font-medium text-white mt-2 inline md:hidden">{t('performance.avgOpp')}</span>
            </div>
            {isLoading ? (
              <div className="animate-pulse dark:bg-slate-400 w-6 h-3 rounded-full"></div>
            ) : (
              <span className="text-base font-semibold md:text-sm md:font-normal text-white">
                {isSingles ? userStats?.singles?.averageOpponentDupr : userStats?.doubles?.averageOpponentDupr}
              </span>
            )}
          </div>

          <div className="flex flex-col flex-1 md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center">
              <AutoGraph className="text-white w-6 h-6" />
              <span className="text-xs font-medium md:text-sm md:font-normal text-white mt-2 md:mt-0 md:ml-2 inline">
                {t('performance.halfLife')}
              </span>
            </div>
            {isLoading ? (
              <div className="animate-pulse dark:bg-slate-400 w-6 h-3 rounded-full"></div>
            ) : (
              <span className="text-base font-semibold md:text-sm md:font-normal text-white">
                {isSingles ? userStats?.singles?.halfLife : userStats?.doubles?.halfLife}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
